/**
 * SectionsPanel — lists the page sections in order. Each row exposes a visibility toggle,
 * a drag handle for reordering, and a click-through to that section's editor. Navbar is
 * anchored first and Footer last (rendered as static, non-draggable rows); everything
 * between is a real drag-and-drop list (Framer Motion `Reorder`) that persists through the
 * same `reorderSections` setter every drop uses — so Live Preview updates on every step of
 * a drag, not just on release. Every action edits the live working schema.
 *
 * Visual-only otherwise — no schema, ordering model, or rendering changed by this component
 * beyond how reordering is triggered. Each kind gets its own outline icon AND a soft, pastel
 * accent color (icon + icon-container tint only — the rest of the row stays neutral), so the
 * list reads as a modern design tool's section list rather than an admin CRUD table. The
 * active row always overrides its kind's accent with the app's brand color instead, so
 * "selected" is unambiguous no matter which kind it is.
 */
import type { ComponentType, CSSProperties, PointerEvent as ReactPointerEvent } from 'react'
import { Reorder, useDragControls } from 'framer-motion'
import {
  Eye,
  EyeOff,
  ChevronRight,
  GripVertical,
  Compass,
  Image,
  FileText,
  Sparkles,
  Images,
  Star,
  HelpCircle,
  MapPin,
  PanelBottom,
} from 'lucide-react'
import { BRAND } from '../../config'
import { useBuilderEditor, SECTION_KIND_LABEL as KIND_LABEL } from './editorContext'
import type { Section, SectionKind } from '../../site-builder/schema'

/** One consistent, elegant outline icon per section kind (lucide — stroke-only, never filled). */
const KIND_ICON: Record<SectionKind, ComponentType<{ className?: string; style?: CSSProperties }>> = {
  navbar: Compass,
  hero: Image,
  about: FileText,
  services: Sparkles,
  gallery: Images,
  reviews: Star,
  faq: HelpCircle,
  contact: MapPin,
  footer: PanelBottom,
}

/** A soft, pastel accent per kind — confined to the icon + its container tint, never the
 * rest of the row, so the list feels lively without becoming noisy. */
const KIND_ACCENT: Record<SectionKind, { bg: string; hoverBg: string; text: string; border: string }> = {
  navbar: { bg: 'bg-blue-50', hoverBg: 'group-hover:bg-blue-100/70', text: 'text-blue-600', border: 'border-blue-100' },
  hero: { bg: 'bg-purple-50', hoverBg: 'group-hover:bg-purple-100/70', text: 'text-purple-600', border: 'border-purple-100' },
  about: { bg: 'bg-indigo-50', hoverBg: 'group-hover:bg-indigo-100/70', text: 'text-indigo-600', border: 'border-indigo-100' },
  services: { bg: 'bg-orange-50', hoverBg: 'group-hover:bg-orange-100/70', text: 'text-orange-600', border: 'border-orange-100' },
  gallery: { bg: 'bg-emerald-50', hoverBg: 'group-hover:bg-emerald-100/70', text: 'text-emerald-600', border: 'border-emerald-100' },
  reviews: { bg: 'bg-amber-50', hoverBg: 'group-hover:bg-amber-100/70', text: 'text-amber-600', border: 'border-amber-100' },
  faq: { bg: 'bg-teal-50', hoverBg: 'group-hover:bg-teal-100/70', text: 'text-teal-600', border: 'border-teal-100' },
  contact: { bg: 'bg-rose-50', hoverBg: 'group-hover:bg-rose-100/70', text: 'text-rose-600', border: 'border-rose-100' },
  footer: { bg: 'bg-slate-50', hoverBg: 'group-hover:bg-slate-100/70', text: 'text-slate-600', border: 'border-slate-200' },
}

/** `BRAND.red` as a soft tint/border, computed once — the same brand-color language the
 * rest of the Builder shell (top workspace nav, Save button) already uses for "active". */
function hexToRgba(hex: string, alpha: number): string {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}
const ACTIVE_TINT_BG = hexToRgba(BRAND.red, 0.08)
const ACTIVE_TINT_BORDER = hexToRgba(BRAND.red, 0.18)

function RowContent({
  section,
  active,
  onOpen,
  onToggleVisible,
  dragHandle,
}: {
  section: Section
  active: boolean
  onOpen: () => void
  onToggleVisible: () => void
  dragHandle: React.ReactNode
}) {
  const Icon = KIND_ICON[section.kind]
  const accent = KIND_ACCENT[section.kind]
  const label = KIND_LABEL[section.kind]

  return (
    <div
      className={`group flex items-center gap-1.5 rounded-2xl px-1.5 py-1.5 transition-all duration-200 ${
        active ? 'shadow-sm' : 'hover:bg-gray-50 hover:shadow-sm'
      }`}
      style={active ? { backgroundColor: ACTIVE_TINT_BG } : undefined}
    >
      {dragHandle}
      <button
        type="button"
        onClick={onOpen}
        className="flex flex-1 cursor-pointer items-center gap-3.5 rounded-xl py-1 pl-1 pr-1 text-left"
      >
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-all duration-200 ${
            active ? '' : `${accent.bg} ${accent.border} ${accent.hoverBg} group-hover:shadow-sm`
          }`}
          style={active ? { backgroundColor: ACTIVE_TINT_BG, borderColor: ACTIVE_TINT_BORDER } : undefined}
        >
          <Icon
            className={`h-4.5 w-4.5 transition-transform duration-200 group-hover:scale-105 ${active ? '' : accent.text}`}
            style={active ? { color: BRAND.red } : undefined}
          />
        </span>
        <span
          className={`flex-1 truncate text-sm leading-tight transition-colors duration-200 ${
            active ? 'font-semibold' : section.visible ? 'font-medium text-gray-800 group-hover:text-gray-900' : 'font-medium text-gray-400'
          }`}
          style={active ? { color: BRAND.red } : undefined}
        >
          {label}
        </span>
        <ChevronRight
          className={`h-4 w-4 shrink-0 text-gray-300 transition-transform duration-200 ${active ? 'rotate-90 text-gray-400' : 'group-hover:translate-x-0.5'}`}
        />
      </button>

      <button
        type="button"
        onClick={onToggleVisible}
        aria-label={section.visible ? `Hide ${label}` : `Show ${label}`}
        aria-pressed={section.visible}
        className="shrink-0 cursor-pointer rounded-lg p-1.5 text-gray-400 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-700"
      >
        {section.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
      </button>
    </div>
  )
}

/** A movable row: the whole card is draggable, but the drag GESTURE only starts from the
 * grip handle (`dragListener={false}` + manual `dragControls.start`) — so clicking the row
 * to open its editor never gets mistaken for a drag. */
function DraggableRow({
  section,
  active,
  onOpen,
  onToggleVisible,
}: {
  section: Section
  active: boolean
  onOpen: () => void
  onToggleVisible: () => void
}) {
  const dragControls = useDragControls()
  const startDrag = (event: ReactPointerEvent) => dragControls.start(event)

  return (
    <Reorder.Item
      as="div"
      value={section}
      dragListener={false}
      dragControls={dragControls}
      whileDrag={{ scale: 1.02, opacity: 0.9, zIndex: 10, boxShadow: '0 10px 24px -4px rgba(0,0,0,0.18)' }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      className="rounded-2xl"
    >
      <RowContent
        section={section}
        active={active}
        onOpen={onOpen}
        onToggleVisible={onToggleVisible}
        dragHandle={
          <button
            type="button"
            onPointerDown={startDrag}
            aria-label={`Drag to reorder ${KIND_LABEL[section.kind]}`}
            className="shrink-0 cursor-grab touch-none rounded-lg p-1 text-gray-300 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-500 active:cursor-grabbing"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        }
      />
    </Reorder.Item>
  )
}

/** A static, anchored row (navbar/footer) — same look, but never draggable, so the grip
 * slot is left as blank spacing rather than a control that would do nothing. */
function AnchoredRow({
  section,
  active,
  onOpen,
  onToggleVisible,
}: {
  section: Section
  active: boolean
  onOpen: () => void
  onToggleVisible: () => void
}) {
  return (
    <RowContent
      section={section}
      active={active}
      onOpen={onOpen}
      onToggleVisible={onToggleVisible}
      dragHandle={<span className="h-6 w-6 shrink-0" aria-hidden="true" />}
    />
  )
}

export function SectionsPanel({ onOpen, activeSectionId }: { onOpen: (sectionId: string) => void; activeSectionId?: string | null }) {
  const e = useBuilderEditor()
  const sections = e.schema.pages[0]?.sections ?? []
  const lastIndex = sections.length - 1

  const navbar = sections[0]?.kind === 'navbar' ? sections[0] : null
  const footer = sections[lastIndex]?.kind === 'footer' ? sections[lastIndex] : null
  const movable = sections.filter((s) => s.kind !== 'navbar' && s.kind !== 'footer')

  return (
    <div className="flex flex-col gap-1 p-2.5">
      {navbar ? (
        <AnchoredRow
          section={navbar}
          active={navbar.id === activeSectionId}
          onOpen={() => onOpen(navbar.id)}
          onToggleVisible={() => e.toggleSectionVisible(navbar.id)}
        />
      ) : null}

      <Reorder.Group
        as="div"
        axis="y"
        values={movable}
        onReorder={(next) => e.reorderSections(next.map((s) => s.id))}
        className="flex flex-col gap-1"
      >
        {movable.map((s) => (
          <DraggableRow
            key={s.id}
            section={s}
            active={s.id === activeSectionId}
            onOpen={() => onOpen(s.id)}
            onToggleVisible={() => e.toggleSectionVisible(s.id)}
          />
        ))}
      </Reorder.Group>

      {footer ? (
        <AnchoredRow
          section={footer}
          active={footer.id === activeSectionId}
          onOpen={() => onOpen(footer.id)}
          onToggleVisible={() => e.toggleSectionVisible(footer.id)}
        />
      ) : null}
    </div>
  )
}
